import React, { Suspense, lazy, useState, useEffect } from "react";
import { Wallet, DollarSign, Utensils, Home } from "lucide-react";
import { filterTabs } from "../lib/permissions.js";

const ExpenseTracker = lazy(() => import("./ExpenseTracker"));
const FoodTracker = lazy(() => import("./FoodTracker"));
const AccountBalance = lazy(() => import("./AccountBalance"));
const ExpenseDienNuoc = lazy(() => import("./ExpenseDienNuoc"));

// Preload tất cả lazy chunks ngay khi module được import — tránh chờ download JS khi chuyển tab
const preloadAll = () => {
    import("./ExpenseTracker");
    import("./FoodTracker");
    import("./AccountBalance");
    import("./ExpenseDienNuoc");
};
preloadAll(); // Gọi trực tiếp để bắt đầu preloading các components ngầm

function TabLoading() {
    return (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="relative w-9 h-9">
                <div className="absolute inset-0 rounded-full bg-indigo-500/10 animate-ping" />
                <div className="w-9 h-9 rounded-full border-[2.5px] border-indigo-500/20 border-t-indigo-500 animate-spin" />
            </div>
            <p className="text-[11px] font-semibold text-slate-400 tracking-wide">Đang tải dữ liệu...</p>
        </div>
    );
}

const TABS = [
    { id: "expenses", label: "Thu Chi", icon: DollarSign },
    { id: "balance", label: "Tài khoản", icon: Wallet },
    { id: "food", label: "Ăn uống", icon: Utensils },
    { id: "diennuoc", label: "Tiền nhà", icon: Home },
];

const PersonalHub = ({ user, token }) => {
    const [activeTab, setActiveTab] = useState(
        () => localStorage.getItem("hagent_personal_tab") || "expenses"
    );

    const visibleTabs = filterTabs(user, "personal", TABS);
    const effectiveTab = visibleTabs.some(t => t.id === activeTab) ? activeTab : visibleTabs[0]?.id;

    const selectTab = (tabId) => {
        setActiveTab(tabId);
        localStorage.setItem("hagent_personal_tab", tabId);
    };

    return (
        <div className="relative flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-50/40 via-white to-white dark:!bg-[#161616] dark:!bg-none">
            {/* Tab Bar - glassmorphism */}
            <div className="sticky top-0 z-20 shrink-0 border-b border-slate-200/60 dark:border-gray-800 bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl px-4 py-3 sm:px-6">
                <div className="inline-flex p-1 bg-slate-100/80 dark:bg-zinc-900/80 backdrop-blur rounded-2xl select-none overflow-x-auto no-scrollbar gap-1 ring-1 ring-slate-200/40 dark:ring-zinc-800/60">
                    {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = effectiveTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => selectTab(tab.id)}
                                className={`
                                    relative flex shrink-0 items-center justify-center gap-1.5 px-3.5 sm:px-4 py-1.5 rounded-xl text-[12px] font-bold
                                    transition-all duration-300 select-none cursor-pointer
                                    ${isActive
                                        ? "bg-white dark:bg-zinc-800 text-indigo-600 dark:text-indigo-400 shadow-[0_2px_8px_-2px_rgba(99,102,241,0.12)] ring-1 ring-slate-200/30 dark:ring-zinc-700/50"
                                        : "text-slate-500 dark:text-zinc-400 hover:text-slate-800 dark:hover:text-zinc-200 hover:bg-white/50 dark:hover:bg-zinc-800/40"
                                    }
                                `}
                            >
                                <Icon size={13} className={isActive ? "text-indigo-500 dark:text-indigo-400" : "text-slate-400 dark:text-zinc-500"} strokeWidth={2.5} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
                <Suspense fallback={<TabLoading />}>
                    {effectiveTab === "expenses" && <ExpenseTracker user={user} token={token} />}
                    {effectiveTab === "balance" && <AccountBalance user={user} token={token} />}
                    {effectiveTab === "food" && <FoodTracker token={token} />}
                    {effectiveTab === "diennuoc" && <ExpenseDienNuoc user={user} token={token} />}
                </Suspense>
            </div>
        </div>
    );
};

export default PersonalHub;
